import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PredefinedJob from "../api/PredefinedJob";
import { emitCustomEvent } from "react-custom-events";
import { toast } from "react-hot-toast";
import Input from "../components/form/Input";
import Select from "../components/form/Select";
import IconSelect from "../components/form/IconSelect";
import { Link, useNavigate } from "react-router-dom";
import ReactTags from "react-tag-autocomplete";
import Certification from "../api/Certification";
import { DashboardLayout } from "../components/organisms/Layout";
import Category from "../api/Category";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./JobPosition.scss";
import { useSelector } from "react-redux";
import { darkmode } from "../store/authSlice";

const AddPredefinedJob = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const mode = useSelector(darkmode);

  const [title, setTitle] = useState("");
  const [startDate, setStartDate] = useState("2033-12-12"); // TODO: Remove after endpoint removes required property
  const [position, setPosition] = useState("full-time");
  const [description, setDescription] = useState("");
  const [experience, setExperience] = useState("");
  const [selectedIcon, setSelectedIcon] = useState("");
  const [certifications, setCertifications] = useState([]);
  const [selectedCertifications, setSelectedCertifications] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  const addPredefinedJob = () => {
    setLoading(true);

    PredefinedJob.add({
      title: title,
      start_date: startDate, // TODO: Remove after endpoint removes required property
      availability: position,
      description: description,
      experience: experience,
      category_id: selectedCategory,
      icon_name: selectedIcon,
    })
      .then(() => {
        emitCustomEvent("closeModal");
        emitCustomEvent("dataChanged");
        toast.success(t("pre-defined-jobs.pre-defined-job-added"));
        navigate("/predefined-jobs");
      })
      .catch((error) => {
        if (error.response && error.response.status === 422) {
          setValidationErrors(error.response.data.errors);
        } else {
          if (error?.response?.status !== 403) {
            toast.error(t("common.something-went-wrong-please-try-again"));
          }
        }
      })
      .then(() => setLoading(false));
  };

  const loadCertifications = () => {
    setLoading(true);

    Certification.getAll()
      .then((response) => {
        setCertifications(response.data.data);
      })
      .catch()
      .then(() => setLoading(false));
  };

  const loadCategories = () => {
    Category.getAll().then((response) => {
      setCategories(response.data.data);
      setSelectedCategory([parseInt(response.data.data[0].id)]);
    });
  };

  const addCertification = (certification) => {
    // Make a request to the api
    Certification.add({
      category_id: selectedCategory || [1],
      name: certification.name,
    }).then(() => {
      setSelectedCertifications([...selectedCertifications, certification]);
    });
  };

  const removeCertification = (index) => {
    const copy = selectedCertifications.slice(0);
    copy.splice(index, 1);
    setSelectedCertifications(copy);
  };

  useEffect(() => {
    document.title = "HeyHire Portal-Create New Position Template";
    loadCertifications();
    loadCategories();
  }, []);

  return (
    <DashboardLayout >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center">
            <Link
              to="/predefined-jobs"
              className="mr-4 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <i className="far fa-chevron-left text-xl" />
            </Link>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {t("pre-defined-jobs.create-new-position-template")}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Create a new predefined job position template.
              </p>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white p-4">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              addPredefinedJob();
            }}
            className="space-y-8"
          >
            {/* Basic Information Section */}
            <div>
              <h3 className="text-base font-semibold leading-7 text-gray-900">
                {t("pre-defined-jobs.basic-information")}
              </h3>
              <p className="mt-1 text-sm leading-6 text-gray-600">
                Basic details about the job position.
              </p>

              <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2">
                <div className="col-span-full sm:col-span-1">
                  <Input
                    name="title"
                    label={t("pre-defined-jobs.job-title")}
                    type="text"
                    value={title}
                    placeholder={t("pre-defined-jobs.job-title")}
                    error={validationErrors?.title?.[0]}
                    onChange={(e) => setTitle(e.target.value)}
                    
                  />
                </div>

                <div className="col-span-full sm:col-span-1">
                  <Select
                    label={t("common.category")}
                    value={selectedCategory}
                    name="category"
                    error={validationErrors?.category_id?.[0]}
                    onChange={(e) => {
                      setSelectedCategory([parseInt(e.target.value)]);
                    }}
                  >
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="col-span-full sm:col-span-1">
                  <Select
                    label={t("pre-defined-jobs.type-of-position")}
                    value={position}
                    name="position"
                    onChange={(e) => setPosition(e.target.value)}
                  >
                    <option value="full-time">Full Time</option>
                    <option value="part-time">Part Time</option>
                  </Select>
                </div>

                <div className="col-span-full sm:col-span-1">
                  <IconSelect
                    label="Job Icon"
                    value={selectedIcon}
                    onChange={setSelectedIcon}
                    error={validationErrors?.icon_name?.[0]}
                    placeholder="Select an icon..."
                  />
                </div>
              </div>
            </div>

            {/* Job Details Section */}
            <div>
              <h3 className="text-base font-semibold leading-7 text-gray-900">
                {t("pre-defined-jobs.job-details")}
              </h3>
              <p className="mt-1 text-sm leading-6 text-gray-600">
                Detailed information about the position.
              </p>

              <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2">
                <div className="col-span-full sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-900">
                    {t("pre-defined-jobs.description")}
                  </label>
                  <div className="mt-2">
                    <ReactQuill
                      className="quill-light"
                      theme="snow"
                      value={description}
                      onChange={setDescription}
                      placeholder={t("pre-defined-jobs.description")}
                    />
                    {validationErrors?.description && (
                      <p className="mt-2 text-sm text-red-600">
                        {validationErrors.description[0]}
                      </p>
                    )}
                  </div>
                </div>

                <div className="col-span-full sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-900">
                    {t("pre-defined-jobs.required-experience")}
                  </label>
                  <div className="mt-2">
                    <ReactQuill
                      className="quill-light"
                      theme="snow"
                      value={experience}
                      onChange={setExperience}
                      placeholder={t("pre-defined-jobs.required-experience")}
                    />
                    {validationErrors?.experience && (
                      <p className="mt-2 text-sm text-red-600">
                        {validationErrors.experience[0]}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Certifications Section */}
            <div>
              <h3 className="text-base font-semibold leading-7 text-gray-900">
                {t("pre-defined-jobs.certifications")}
              </h3>
              <p className="mt-1 text-sm leading-6 text-gray-600">
                Required certifications for this position.
              </p>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-900">
                  {t("pre-defined-jobs.required-certifications")}
                </label>
                <div className="mt-2 predefined-job-tags">
                  <ReactTags
                    tags={selectedCertifications}
                    suggestions={certifications}
                    onDelete={removeCertification}
                    onAddition={addCertification}
                    newTagText={"Add New Certification:"}
                    placeholderText={"Search certifications or add your own"}
                    allowNew={true}
                    allowBackspace={false}
                    classNames={{
                      root:
                        "react-tags w-full px-3 py-2 rounded-md border border-gray-300 bg-white shadow-sm transition-colors hover:border-gray-400",
                      rootFocused: "ring-2 ring-primary-500 border-primary-400",
                      selected: "react-tags__selected",
                      search: "react-tags__search relative",
                      searchWrapper: "react-tags__search-wrapper",
                      searchInput:
                        "react-tags__search-input text-gray-900 placeholder:text-gray-400 placeholder:text-sm",
                      suggestions:
                        "react-tags__suggestions absolute left-0 top-full z-[100] mt-0.5 max-h-48 min-w-full w-max max-w-[min(100vw-2rem,24rem)] overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg",
                      suggestionActive: "bg-gray-100",
                      suggestionDisabled: "opacity-50 cursor-not-allowed",
                    }}
                    tagComponent={({ tag, onDelete }) => (
                      <button
                        type="button"
                        className="inline-flex items-center px-3 py-1 mr-0 text-sm font-medium text-white bg-primary-500 rounded-full hover:bg-primary-600"
                        onClick={onDelete}
                      >
                        {tag.name}
                        <i className="fas fa-times-circle ml-2" />
                      </button>
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="border-t border-gray-900/10">
              <button
                type="button"
                onClick={() => {
                  setTitle("");
                  setDescription("");
                  setExperience("");
                  setSelectedIcon("");
                  setSelectedCertifications([]);
                  setPosition("full-time");
                }}
                className="rounded-lg px-4 py-2.5 text-sm font-semibold text-gray-900"
              >
                {t("common.clear")}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="rounded-lg bg-primary-500 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px]"
              >
                {loading ? (
                  <i className="fad fa-circle-notch fa-spin" />
                ) : (
                  t("common.save")
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddPredefinedJob;
